/**
 * Representa o contexto da função getElevation.
 */
export interface ElevationContext {
  /**
   * O elemento de scopo onde a função procura.
   *
   * @default document.body
   */
  scope?: HTMLElement;
  /**
   * O elemento para ser excluído da procura do z-index.
   */
  except?: HTMLElement;
}

/**
 * Procura recursivamente o z-index do elemento e seus filhos.
 *
 * @param element - O elemento para iniciar a procura.
 * @param except - O elemento para ser excluído da procura do z-index.
 */
const findZIndexes = (element: HTMLElement, except?: HTMLElement): number[] => {
  const zIndexes: number[] = [];
  const stack: HTMLElement[] = [element];

  while (stack.length > 0) {
    const currentElement = stack.pop()!;
    const styleZIndex = currentElement.style.zIndex;
    const computedZIndex = getComputedStyle(currentElement).getPropertyValue('z-index');

    if (styleZIndex && styleZIndex !== 'auto' && currentElement !== except) {
      zIndexes.push(Number(styleZIndex));
    }

    if (computedZIndex && computedZIndex !== 'auto' && currentElement !== except) {
      zIndexes.push(Number(computedZIndex));
    }

    const children = Array.from(currentElement.children) as HTMLElement[];

    stack.push(...children);
  }

  return zIndexes;
};

/**
 * Retorna o maior zIndex dentro do escopo especificado.
 * Se o escopo não for especificado, a função irá procurar dentro do body.
 * Se o elemento para ser excluído da procura do z-index for especificado, a função irá ignorá-lo.
 *
 * @param {ElevationContext} - Representa o contexto da função getElevation.
 * @returns {number} Retorna o maior zIndex dentro do escopo especificado.
 *
 * @example
 * ```typescript
 * // Retorna o maior z-index dentro do body.
 * const result = getElevation();
 *
 * // Retorna o maior z-index em/dentro o/de elemento específico.
 * const result = getElevation({ scope: document.getElementById('container') as HTMLElement });
 *
 * // Retorna o maior z-index em/dentro o/do body, ignorando o elemento especificado.
 * const result = getElevation({ except: document.getElementById('element2') as HTMLElement });
 * ```
 */
export const getElevation = ({ scope = document.body, except }: ElevationContext = {}): number => {
  const zIndexes: number[] = findZIndexes(scope, except);

  /* istanbul ignore next */
  return zIndexes.length > 0 ? Math.max(...zIndexes) + 1 : 1;
};
